import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { ImageModule } from 'primeng/image';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import { ShipmentWaypoint } from '../../models/waypoint/shipment.waypoint';
import { ShipmentDeliveryStop } from '../../models/workflow/shipment-delivery-stop.model';

export type ShipmentRouteCommand =
    | { type: 'save-order' }
    | { type: 'cancel-order' }
    | { type: 'optimize' }
    | { type: 'start-manual-order' }
    | { type: 'add-driver-home-return' }
    | { type: 'open-return-location' }
    | { type: 'add-first-stop' }
    | { type: 'move-waypoint'; index: number; direction: -1 | 1 }
    | { type: 'share-waypoint'; waypoint: ShipmentWaypoint }
    | { type: 'edit-waypoint'; waypoint: ShipmentWaypoint }
    | { type: 'add-waypoint-after'; waypoint: ShipmentWaypoint };

/** Route presentation and explicit intents only; all waypoint mutations remain in the shipment shell. */
@Component({
    selector: 'app-shipment-route-panel',
    standalone: true,
    templateUrl: './shipment-route-panel.component.html',
    styleUrls: ['./shipment-route-panel.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ButtonModule, ImageModule, TagModule, TooltipModule]
})
export class ShipmentRoutePanelComponent {
    readonly waypoints = input<ShipmentWaypoint[]>([]);
    readonly deliveryRoutes = input<readonly { deliveryNumber: string; stops: ShipmentDeliveryStop[] }[]>([]);
    readonly manualOrderMode = input(false);
    readonly orderDirty = input(false);
    readonly orderSaving = input(false);
    readonly addingWaypoint = input(false);
    readonly canCreateWaypoints = input(false);
    readonly canEditWaypoints = input(false);
    readonly hasReturnWaypoint = input(false);
    readonly returnStatusLabel = input('Optional return stop not added');
    readonly returnStatusSeverity = input('secondary');
    readonly driverResidenceLabel = input('No confirmed driver');
    readonly canAddDriverHomeReturn = input(false);
    readonly mapUrl = input('');
    readonly routeDistanceLabel = input('Not calculated');
    readonly routeDurationLabel = input('Not calculated');
    readonly waypointDisplayType = input<(waypoint: ShipmentWaypoint) => string>(() => 'Stop');
    readonly waypointTypeSeverity = input<(waypoint: ShipmentWaypoint) => string>(() => 'secondary');
    readonly waypointMapsUrl = input<(waypoint: ShipmentWaypoint) => string | null>(() => null);
    readonly waypointAddress = input<(waypoint: ShipmentWaypoint) => string>(() => 'Open in Maps');
    readonly waypointSegmentDistance = input<(index: number) => string>(() => 'Distance pending');
    readonly waypointSegmentDuration = input<(index: number) => string>(() => 'Time pending');
    readonly command = output<ShipmentRouteCommand>();
}
